import groq from 'groq';
import { defaultLocale } from '@pkg/config/languages';
import { BUSINESS_AUDIENCE_FILTER, SITE_IDS } from '@pkg/config/sanity/audience';
import { SINGLETON } from '@pkg/config/sanity/schemas';
import { AppStore } from '@pkg/sanity-utils/constants/appstore';
import { defaultSeoFields } from '@pkg/sanity-utils/queries/defaultSeoFields';
import { seoConfigDocumentId } from '@pkg/sanity-utils/seoConfigDocumentId';
import type { DefaultSeo } from '@pkg/sanity-utils/types/seo';
import type { AppStoresConfig } from '@pkg/modular-blocks/frontend/appStores';
import {
  footerNavigationItems,
  mainNavFields,
  type FooterNavigation,
  type MainNav,
} from '@pkg/site-chrome';

export interface BusinessSiteManifest {
  regionTheme: {
    mainNavigation: MainNav | null;
    mainFooter: FooterNavigation | null;
  } | null;
  /** Shared WWW `config.appstores` singleton — download URLs/badges for header CTAs. */
  appStores: AppStoresConfig | null;
  /** Business SEO + Social singleton (`singleton-config_seo-business`). */
  defaultSeo: DefaultSeo | null;
}

/**
 * Business chrome from the fixed business Theme singleton only
 * (`singleton-theme-{locale}-business`), never the www Theme via `[0]`.
 * Audience filter is a safety net if the id exists but is mistagged.
 *
 * Params: `{ themeId, locale }` —
 * `themeId` from `singletonDocId(THEME, locale, 'business')`;
 * `locale` required by nav portable-text / dynamicText projections.
 */
export const businessSiteManifest = groq`{
  "regionTheme": *[
    _id == $themeId &&
    ${BUSINESS_AUDIENCE_FILTER}
  ][0] {
    "mainNavigation": mainNav->{
      ${mainNavFields}
    },
    "mainFooter": mainFooter->{
      ${footerNavigationItems}
    },
  },
  "appStores": *[_type == "${SINGLETON.CONFIG_APPSTORES}"][0] {
    "modalTitleAppStoreDownload": coalesce(
      modalTitleAppStoreDownload[language == $locale][0].value,
      modalTitleAppStoreDownload[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.APPLE_APP_STORE}": coalesce(
      ${AppStore.APPLE_APP_STORE}[language == $locale][0].value,
      ${AppStore.APPLE_APP_STORE}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.GOOGLE_PLAY_STORE}": coalesce(
      ${AppStore.GOOGLE_PLAY_STORE}[language == $locale][0].value,
      ${AppStore.GOOGLE_PLAY_STORE}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.HUAWEI_APP_GALLERY}": coalesce(
      ${AppStore.HUAWEI_APP_GALLERY}[language == $locale][0].value,
      ${AppStore.HUAWEI_APP_GALLERY}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.APPLE_APP_STORE_BADGE}": coalesce(
      ${AppStore.APPLE_APP_STORE_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.APPLE_APP_STORE_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
    "${AppStore.GOOGLE_PLAY_STORE_BADGE}": coalesce(
      ${AppStore.GOOGLE_PLAY_STORE_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.GOOGLE_PLAY_STORE_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
    "${AppStore.HUAWEI_APP_GALLERY_BADGE}": coalesce(
      ${AppStore.HUAWEI_APP_GALLERY_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.HUAWEI_APP_GALLERY_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
  },
  "defaultSeo": *[_id == "${seoConfigDocumentId(SITE_IDS.BUSINESS)}"][0] {
    ${defaultSeoFields}
  },
}`;
